// app/src/lib/procurement/priceBook.js — PriceEntry log + history queries (US-2.5, US-2.6).
//
// Prices are stored as integer cents per the vendor's UOM, plus a derived
// `unitPriceCents` (per base unit) for cross-pack comparison when pack-size
// metadata is available.

import { db } from '../db.js';
import { vendorLabel } from '../demo.js';
import { requireUserId, cents, isoToday } from './util.js';

/** @typedef {{ priceEntryId:string, userId:string, vendorId:string, itemId:string, vendorItemId?:string, priceCents:number, unitPriceCents?:number, packSize?:string, uom:string, effectiveDate:string, source:'ocr'|'manual'|'contract', invoiceId?:string, contractId?:string }} PriceEntry */

/**
 * Log a price observation. Caller is expected to pass dollars; converted to cents here.
 *
 * @param {{ vendorId:string, itemId:string, vendorItemId?:string, price:number, packSize?:string, uom:string, effectiveDate?:string, source?:'ocr'|'manual'|'contract', invoiceId?:string, contractId?:string }} input
 * @returns {Promise<PriceEntry>}
 */
export async function logEntry(input) {
  const userId = await requireUserId();
  if (!input.vendorId || !input.itemId) throw new Error('priceBook: vendorId+itemId required');
  if (!Number.isFinite(input.price) || input.price < 0) throw new Error('priceBook: invalid price');
  /** @type {PriceEntry} */
  const entry = {
    priceEntryId: crypto.randomUUID(),
    userId,
    vendorId: input.vendorId,
    itemId: input.itemId,
    vendorItemId: input.vendorItemId,
    priceCents: cents(input.price),
    unitPriceCents: parsePerUnit(input.price, input.packSize),
    packSize: input.packSize,
    uom: input.uom || 'each',
    effectiveDate: input.effectiveDate || isoToday(),
    source: input.source || 'manual',
    invoiceId: input.invoiceId,
    contractId: input.contractId,
  };
  await db.priceEntries.put(entry);
  return entry;
}

/** Most recent price entry for (vendor, item). */
export async function latestFor(vendorId, itemId) {
  const userId = await requireUserId();
  const rows = await db.priceEntries
    .where('[vendorId+itemId+effectiveDate]')
    .between([vendorId, itemId, ''], [vendorId, itemId, '￿'])
    .toArray();
  if (!rows.length) return null;
  const filtered = rows.filter((r) => r.userId === userId);
  if (!filtered.length) return null;
  filtered.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
  return filtered[0];
}

/** Last 12 months of entries for an item across all vendors. */
export async function history(itemId, { months = 12 } = {}) {
  const userId = await requireUserId();
  const cutoff = new Date(); cutoff.setUTCMonth(cutoff.getUTCMonth() - months);
  const cutoffIso = cutoff.toISOString().slice(0, 10);
  return db.priceEntries
    .where('[itemId+effectiveDate]')
    .between([itemId, cutoffIso], [itemId, '￿'])
    .filter((r) => r.userId === userId)
    .toArray();
}

/** "Last best price": lowest unit price paid in the last 12 months for an item. */
export async function lastBestPrice(itemId) {
  const rows = await history(itemId, { months: 12 });
  if (!rows.length) return null;
  let best = null;
  for (const r of rows) {
    const compare = r.unitPriceCents ?? r.priceCents;
    if (!best || compare < (best.unitPriceCents ?? best.priceCents)) best = r;
  }
  return best;
}

/**
 * Multi-vendor comparison for one item (US-2.6).
 *
 * @returns {Promise<{ vendorId:string, vendorName:string, currentPriceCents:number, unitPriceCents:number|null, packSize?:string, lastPurchased:string|null, isCheapest:boolean }[]>}
 */
export async function compareVendors(itemId) {
  const userId = await requireUserId();
  const vendorItems = await db.vendorItems.where('itemId').equals(itemId).filter((vi) => vi.userId === userId && vi.isActive === 1).toArray();
  const vendorIds = Array.from(new Set(vendorItems.map((v) => v.vendorId)));

  const rows = await Promise.all(vendorIds.map(async (vid) => {
    const latest = await latestFor(vid, itemId);
    if (!latest) return null;
    const vendor = await db.vendors.get(vid);
    if (!vendor || vendor.isActive !== 1) return null;
    return {
      vendorId: vid,
      vendorName: vendorLabel(vendor.name),
      currentPriceCents: latest.priceCents,
      unitPriceCents: latest.unitPriceCents ?? null,
      packSize: latest.packSize,
      lastPurchased: latest.effectiveDate,
      isCheapest: false,
    };
  }));

  const filtered = rows.filter(Boolean);
  if (filtered.length === 0) return [];
  // "Cheapest" determined by unit price when available, else by raw price.
  const cheapest = filtered.reduce((min, r) => {
    const v = r.unitPriceCents ?? r.currentPriceCents;
    const m = min.unitPriceCents ?? min.currentPriceCents;
    return v < m ? r : min;
  });
  cheapest.isCheapest = true;
  return filtered.sort((a, b) => (a.unitPriceCents ?? a.currentPriceCents) - (b.unitPriceCents ?? b.currentPriceCents));
}

// ── helpers ────────────────────────────────────────────────────────────────

/**
 * Best-effort pack-size parser: pulls the first integer count and converts the
 * unit price to per-base-unit cents. Examples:
 *   "6/#10 cans" → 6 units per pack
 *   "12 × 24oz"  → 12 units per pack
 *   "5 lb"       → no count, returns undefined
 *
 * If we cannot extract a count we return undefined (caller falls back to priceCents).
 */
function parsePerUnit(price, packSize) {
  if (!packSize) return undefined;
  const m = String(packSize).match(/(\d+)\s*[/×x*]\s*/i);
  if (!m) return undefined;
  const count = Number(m[1]);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  return Math.round((price * 100) / count);
}
